import { isPlainObject } from 'es-toolkit';

const CSS_MAX_PENALTY_SCORE = 18;
const PERCENT_SCALE = 100;
const MAJOR_PENALTY = 2;
const WORKFLOW_PENALTY = 1.5;
const MINOR_PENALTY = 0.5;

export enum UserSatisfactionLevel {
    VeryDissatisfied = 'very dissatisfied',
    Dissatisfied = 'dissatisfied',
    Neutral = 'neutral',
    Satisfied = 'satisfied',
    VerySatisfied = 'very satisfied'
}

export type QualityMetrics = {
    agent_behavior?: {
        agent_contradicting_phrases?: boolean;
        agent_declines_capability?: boolean;
        agent_not_replying?: boolean;
        agent_looping_detected?: boolean;
        agent_asks_already_answered_questions_count?: number;
        agent_reports_technical_issue?: boolean;
        language_switching_behavior?: boolean;
        agent_fails_to_switch_language?: boolean;
        languages_used?: string[];
        agent_checks_users_presence?: boolean;
    };
    scenario_following?: {
        intent_misclassified?: boolean;
        mixed_steps_present?: boolean;
        agent_did_not_follow_workflow?: boolean;
        agent_cannot_switch_scenario?: boolean;
        conversation_termination_type?: string;
    };
    user_behavior?: {
        user_hung_up_due_to_ai_mistake?: boolean;
        user_is_deaf_or_hearing_impaired?: boolean;
        user_checks_agent_presence?: boolean;
        user_corrects_agent?: boolean;
        transfer_to_human_request_count?: number;
        user_uses_obscene_language?: boolean;
        user_reports_no_sms_received?: boolean;
        user_says_no_count?: number;
        user_interruptions_count?: number;
        user_satisfaction_level?: UserSatisfactionLevel | null;
    };
};

export const analyzeQualityMetrics = (
    qualityMetrics?: QualityMetrics | null
): number | undefined => {
    if (qualityMetrics === undefined || qualityMetrics === null) {
        return;
    }

    const hasMetricValues = [
        qualityMetrics.agent_behavior,
        qualityMetrics.scenario_following,
        qualityMetrics.user_behavior
    ].some(
        (metricSection) =>
            isPlainObject(metricSection) &&
            Object.keys(metricSection).length > 0
    );

    if (!hasMetricValues) {
        return;
    }

    const agentBehavior = qualityMetrics.agent_behavior;
    const scenarioFollowing = qualityMetrics.scenario_following;
    const userBehavior = qualityMetrics.user_behavior;
    const userSatisfactionLevel = userBehavior?.user_satisfaction_level;
    const userSatisfactionPenalty =
        userSatisfactionLevel === UserSatisfactionLevel.VeryDissatisfied ||
        userSatisfactionLevel === UserSatisfactionLevel.Dissatisfied
            ? 1
            : 0;

    const totalPenalty =
        Number(agentBehavior?.agent_looping_detected === true) * MAJOR_PENALTY +
        Number(userBehavior?.user_uses_obscene_language === true) *
            MAJOR_PENALTY +
        Number(scenarioFollowing?.agent_did_not_follow_workflow === true) *
            WORKFLOW_PENALTY +
        ((agentBehavior?.agent_asks_already_answered_questions_count ?? 0) > 0
            ? 1
            : 0) +
        ((userBehavior?.transfer_to_human_request_count ?? 0) > 1 ? 1 : 0) +
        Number(agentBehavior?.agent_declines_capability === true) +
        userSatisfactionPenalty +
        Number(agentBehavior?.agent_contradicting_phrases === true) +
        Number(scenarioFollowing?.agent_cannot_switch_scenario === true) +
        Number(userBehavior?.user_checks_agent_presence === true) +
        Number(agentBehavior?.agent_checks_users_presence === true) +
        Number(userBehavior?.user_reports_no_sms_received === true) +
        Number(scenarioFollowing?.intent_misclassified === true) *
            MINOR_PENALTY +
        ((userBehavior?.user_interruptions_count ?? 0) > 0 ? 1 : 0) *
            MINOR_PENALTY +
        Number(userBehavior?.user_corrects_agent === true) * MINOR_PENALTY +
        Number(userBehavior?.user_is_deaf_or_hearing_impaired === true) *
            MINOR_PENALTY +
        Number(userBehavior?.user_hung_up_due_to_ai_mistake === true) *
            MINOR_PENALTY +
        Number(agentBehavior?.agent_reports_technical_issue === true) *
            MINOR_PENALTY +
        Number(agentBehavior?.agent_not_replying === true) * MINOR_PENALTY;

    const score = Math.round(
        PERCENT_SCALE * (1 - totalPenalty / CSS_MAX_PENALTY_SCORE)
    );

    return Math.max(0, Math.min(PERCENT_SCALE, score));
};

export const getConversationSuccessScore = (
    qualityMetricsValue?: string
): number | undefined => {
    if (!qualityMetricsValue) {
        return;
    }

    try {
        return analyzeQualityMetrics(
            JSON.parse(qualityMetricsValue) as QualityMetrics
        );
    } catch {
        return;
    }
};
